import { Delete } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTouchMode } from '../lib/hardware';

/**
 * Teclado numérico en pantalla para computadoras táctiles.
 * Aparece debajo de cualquier campo de números (peso, importe, efectivo, descuento) apenas se toca,
 * dentro de la misma ventana, y evita que salga el teclado chico de Windows.
 */
const NUMERIC = new Set(['decimal', 'numeric']);
/** Recuerda si el campo era de números enteros (PIN, cantidades) antes de apagarle el teclado de Windows. */
function mark(el: HTMLInputElement) {
  if (el.dataset.numpad === '1') return;
  el.dataset.integer = el.inputMode === 'numeric' ? '1' : '0';
  el.dataset.numpad = '1';
  el.inputMode = 'none';
}

const isNumericInput = (el: EventTarget | null): el is HTMLInputElement =>
  el instanceof HTMLInputElement && !el.readOnly && !el.disabled && (NUMERIC.has(el.inputMode) || el.dataset.numpad === '1');

/** Cambia el valor de un campo controlado por React como si se hubiera tipeado. */
function typeInto(el: HTMLInputElement, value: string) {
  const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
  setter?.call(el, value);
  el.dispatchEvent(new Event('input', { bubbles: true }));
}

export function TouchKeypad() {
  const on = useTouchMode();
  const [target, setTarget] = useState<HTMLInputElement | null>(null);
  const [host, setHost] = useState<HTMLElement | null>(null);
  const hostRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    document.documentElement.classList.toggle('bf-touch', on);
    if (!on) return;
    // Antes de que el campo tome el foco: sin teclado de Windows.
    const onDown = (e: PointerEvent) => {
      if (isNumericInput(e.target)) mark(e.target);
    };
    const onFocus = (e: FocusEvent) => {
      const el = e.target;
      if (!isNumericInput(el)) return;
      mark(el);
      // El teclado va debajo del campo, dentro de la misma ventana (si no, tocarlo la cerraría).
      const anchor = el.closest('.bf-field') ?? el.parentElement;
      if (!anchor) return;
      hostRef.current?.remove();
      const div = document.createElement('div');
      div.className = 'bf-keypad-host';
      anchor.after(div);
      hostRef.current = div;
      setHost(div);
      setTarget(el);
    };
    const onBlur = (e: FocusEvent) => {
      if (!isNumericInput(e.target)) return;
      const next = e.relatedTarget as Node | null;
      if (next && hostRef.current?.contains(next)) return;
      // Si el foco pasa a otro campo de números, onFocus arma el teclado de nuevo.
      setTimeout(() => {
        if (isNumericInput(document.activeElement)) return;
        hostRef.current?.remove();
        hostRef.current = null;
        setHost(null);
        setTarget(null);
      }, 0);
    };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('focusin', onFocus);
    document.addEventListener('focusout', onBlur);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('focusin', onFocus);
      document.removeEventListener('focusout', onBlur);
      hostRef.current?.remove();
      hostRef.current = null;
      setHost(null);
      setTarget(null);
    };
  }, [on]);

  if (!on || !target || !host || !host.isConnected) return null;
  const decimal = target.dataset.integer !== '1';

  const press = (k: string) => {
    const v = target.value;
    if (k === 'back') typeInto(target, v.slice(0, -1));
    else if (k === 'clear') typeInto(target, '');
    else if (k === ',') {
      if (decimal && !v.includes(',')) typeInto(target, (v || '0') + ',');
    } else typeInto(target, v + k);
  };
  const keep = (e: React.PointerEvent) => e.preventDefault(); // el campo no pierde el foco

  return createPortal(
    <div className="bf-keypad" role="group" aria-label="Teclado numérico">
      {['7', '8', '9', '4', '5', '6', '1', '2', '3'].map((k) => (
        <button key={k} type="button" onPointerDown={keep} onClick={() => press(k)}>
          {k}
        </button>
      ))}
      <button type="button" onPointerDown={keep} onClick={() => press(',')} disabled={!decimal} aria-label="Coma">
        ,
      </button>
      <button type="button" onPointerDown={keep} onClick={() => press('0')}>
        0
      </button>
      <button type="button" onPointerDown={keep} onClick={() => press('back')} aria-label="Borrar">
        <Delete aria-hidden />
      </button>
      <button type="button" className="bf-keypad-wide" onPointerDown={keep} onClick={() => press('clear')}>
        Borrar todo
      </button>
      <button type="button" className="bf-keypad-ok" onPointerDown={keep} onClick={() => target.form?.requestSubmit()} disabled={!target.form}>
        OK
      </button>
    </div>,
    host,
  );
}
